import { claimDataSource } from '~/api/data-source/claim';
import type { ClaimDataSource } from '~/api/data-source/claim';
import type {
  CancellableProductOrder,
  CancelProductOrdersRequest,
  ClaimDetail,
  ClaimHistoryPage,
  ClaimReasonItem,
  OrderClaim,
  OrderProductItem,
} from '~/api/model';
import {
  cancellableProductOrderSchema,
  cancelProductOrdersRequestSchema,
  claimDetailSchema,
  claimHistoryPageSchema,
  claimReasonItemSchema,
  orderClaimSchema,
  parseModel,
  parseModelArray,
  productOrderSchema,
} from '~/api/model';

export interface ClaimRepository {
  getClaims(params?: {
    lastOfferId?: number;
    size?: number;
  }): Promise<ClaimHistoryPage>;
  getCancellableProductOrders(
    orderId: number,
  ): Promise<CancellableProductOrder>;
  getCanceledProductDetail(productOrderId: number): Promise<OrderClaim>;
  cancelProductOrders(request: CancelProductOrdersRequest): Promise<void>;
  getClaimableProductOrders(
    vendorOrderId: number,
  ): Promise<OrderProductItem[]>;
  getClaimReasons(): Promise<ClaimReasonItem[]>;
  getClaimDetail(claimId: number): Promise<ClaimDetail>;
  createClaim(formData: FormData): Promise<{ claimId?: number }>;
}

export class ClaimRepositoryImpl implements ClaimRepository {
  // eslint-disable-next-line no-useless-constructor
  constructor(private readonly dataSource: ClaimDataSource = claimDataSource) {}

  async getClaims(params?: { lastOfferId?: number; size?: number }) {
    const response = await this.dataSource.getClaims(params);
    return parseModel(claimHistoryPageSchema, response, 'ClaimHistoryPage');
  }

  async getCancellableProductOrders(orderId: number) {
    const response = await this.dataSource.getCancellableProductOrders(orderId);
    return parseModel(
      cancellableProductOrderSchema,
      response,
      'CancellableProductOrder',
    );
  }

  async getCanceledProductDetail(productOrderId: number) {
    const response =
      await this.dataSource.getCanceledProductDetail(productOrderId);
    return parseModel(orderClaimSchema, response, 'OrderClaim');
  }

  async cancelProductOrders(request: CancelProductOrdersRequest) {
    const body = cancelProductOrdersRequestSchema.parse(request);
    await this.dataSource.cancelProductOrders(body);
  }

  async getClaimableProductOrders(vendorOrderId: number) {
    const response =
      await this.dataSource.getClaimableProductOrders(vendorOrderId);
    return parseModelArray(productOrderSchema, response, 'OrderProductItem');
  }

  async getClaimReasons() {
    const response = await this.dataSource.getClaimReasons();
    return parseModelArray(claimReasonItemSchema, response, 'ClaimReasonItem');
  }

  async getClaimDetail(claimId: number) {
    const response = await this.dataSource.getClaimDetail(claimId);
    return parseModel(claimDetailSchema, response, 'ClaimDetail');
  }

  async createClaim(formData: FormData) {
    return this.dataSource.createClaim(formData);
  }
}

export const claimRepository = new ClaimRepositoryImpl();
